import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
  Typography,
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors,
  DragEndEvent 
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import categoryService from '../services/categoryService';
import { Category, NewCategory } from '../models/Category';
import { useAdminScope } from '../contexts/AdminScopeContext';
import ScopeRequirementAlert from '../components/ScopeRequirementAlert';
import {
  isMenuCatalogManagedByPosCaisse,
  MENU_CATALOG_POSCAISSE_MESSAGE,
} from '../utils/menuCatalogOwnership';

// Type pour le formulaire
interface CategoryFormData {
  name: string;
  description: string;
  isActive: boolean;
  order?: number;
  imageFile?: File | null;
  imagePreview?: string;
  isAddonCategory?: boolean;
}

// SortableRow Component - pour le glisser-déposer
interface SortableRowProps {
  id: string;
  category: Category;
  onToggleVisibility: (category: Category) => void;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  readOnly: boolean;
}

const SortableRow: React.FC<SortableRowProps> = ({ 
  id, 
  category, 
  onToggleVisibility, 
  onEdit, 
  onDelete,
  onMoveUp,
  onMoveDown,
  index,
  isFirst,
  isLast,
  readOnly
}) => {
  // Utiliser le hook useSortable pour rendre la ligne déplaçable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled: readOnly });

  // Appliquer les styles de transformation pendant le glisser-déposer
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined
  };
  const imageSrc = category.imageUrl && category.imageVersion
    ? `${category.imageUrl}${category.imageUrl.includes('?') ? '&' : '?'}v=${category.imageVersion}`
    : category.imageUrl;

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell padding="checkbox">
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          disabled={readOnly}
          sx={{ cursor: readOnly ? 'default' : 'grab' }}
        >
          <DragIndicatorIcon />
        </IconButton>
      </TableCell>
      <TableCell>
        {imageSrc ? (
          <Box
            component="img"
            src={imageSrc}
            alt={category.name}
            sx={{ width: 60, height: 60, objectFit: 'contain' }}
          />
        ) : (
          <Box
            sx={{
              width: 60,
              height: 60,
              bgcolor: 'grey.200',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 1
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No image
            </Typography>
          </Box>
        )}
      </TableCell>
      <TableCell>
        <Typography fontWeight="bold">{category.name}</Typography>
      </TableCell>
      <TableCell>{category.description}</TableCell>
      <TableCell>
        {category.isActive ? (
          <Box sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            Visible
          </Box>
        ) : (
          <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
            <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} />
            Masqué
          </Box>
        )}
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Monter">
            <span>
              <IconButton
                size="small"
                color="default"
                onClick={() => onMoveUp(index)}
                disabled={readOnly || isFirst}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Descendre">
            <span>
              <IconButton
                size="small"
                color="default"
                onClick={() => onMoveDown(index)}
                disabled={readOnly || isLast}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            color={category.isActive ? 'default' : 'primary'}
            onClick={() => onToggleVisibility(category)}
            title={category.isActive ? "Masquer" : "Rendre visible"}
            disabled={readOnly}
          >
            {category.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => onEdit(category)}
            title="Modifier"
            disabled={readOnly}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => onDelete(id)}
            title="Supprimer"
            disabled={readOnly}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const Categories: React.FC = () => {
  const { activeScopeType, selectedStoreId, selectedStore } = useAdminScope();
  const isCatalogReadOnly = isMenuCatalogManagedByPosCaisse(selectedStore);
  // État pour la liste des catégories
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // État pour la pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCategories, setTotalCategories] = useState(0);

  // État pour le formulaire de catégorie
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [savingCategory, setSavingCategory] = useState(false);
const [formData, setFormData] = useState<CategoryFormData>({
  name: '',
  description: '',
  isActive: true,
  imageFile: null,
  imagePreview: undefined,
  isAddonCategory: false
});

  // Charger les catégories au chargement de la page
  useEffect(() => {
    if (activeScopeType === 'store' && selectedStoreId) {
      void loadCategories();
      return;
    }

    setCategories([]);
    setTotalCategories(0);
    setLoading(false);
  }, [activeScopeType, selectedStoreId]);

  // Fonction pour charger les catégories
  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    console.log("Categories.tsx: Début du chargement des catégories...");
    try {
      console.log("Categories.tsx: Appel de categoryService.getCategories()");
      const data = await categoryService.getCategories();
      console.log("Categories.tsx: Catégories reçues:", data);
      console.log("Categories.tsx: Nombre de catégories:", data.length);
      
      // Debug: voir les valeurs de chaque catégorie
      data.forEach((cat, index) => {
        console.log(`Categories.tsx: Catégorie ${index}:`, 
          JSON.stringify({
            id: cat.id,
            name: cat.name,
            description: cat.description,
            imageUrl: cat.imageUrl,
            isActive: cat.isActive,
            order: cat.order
          })
        );
      });
      
      setCategories(data);
      setTotalCategories(data.length);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
      setError('Erreur lors du chargement des catégories. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  // Gestionnaire de changement de page
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Gestionnaire de changement du nombre d'éléments par page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Retour à la première page
  };
  
  // Obtenir les catégories de la page courante
  const paginatedCategories = categories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Ouvrir le dialogue pour ajouter une catégorie
  const handleAddCategory = () => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      isActive: true,
      imageFile: null,
      imagePreview: undefined,
      isAddonCategory: false
    });
    setOpenDialog(true);
  };

  // Ouvrir le dialogue pour éditer une catégorie
  const handleEditCategory = (category: Category) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      isActive: category.isActive,
      order: category.order,
      imageFile: null,
      imagePreview: category.imageUrl && category.imageVersion
        ? `${category.imageUrl}${category.imageUrl.includes('?') ? '&' : '?'}v=${category.imageVersion}`
        : category.imageUrl,
      isAddonCategory: category.isAddonCategory ?? false
    });
    setOpenDialog(true);
  };

  // Gérer les changements dans le formulaire
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Gérer la sélection d'une image
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setFormData({
        ...formData,
        imageFile: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async () => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    try {
      setSavingCategory(true);
      if (editingCategory) {
        // Mise à jour d'une catégorie existante
await categoryService.updateCategory(
            editingCategory.id,
            {
              name: formData.name,
              description: formData.description,
              isActive: formData.isActive,
              isAddonCategory: formData.isAddonCategory,
              order: formData.order !== undefined ? Number(formData.order) : undefined
            },
          formData.imageFile || undefined
        );
      } else {
        // Création d'une nouvelle catégorie
const newCategory: NewCategory = {
            name: formData.name,
            description: formData.description,
            isActive: formData.isActive,
            isAddonCategory: formData.isAddonCategory,
            order: formData.order !== undefined ? Number(formData.order) : undefined
          };
        await categoryService.createCategory(newCategory, formData.imageFile || undefined);
      }

      // Recharger les catégories
      await loadCategories();
      
      // Fermer le dialogue
      setOpenDialog(false);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la catégorie:', err);
      setError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setSavingCategory(false);
    }
  };

  // Supprimer une catégorie
  const handleDeleteCategory = async (id: string) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await categoryService.deleteCategory(id);
        await loadCategories();
      } catch (err) {
        console.error('Erreur lors de la suppression de la catégorie:', err);
        setError('Erreur lors de la suppression. Veuillez réessayer.');
      }
    }
  };

  // Changer la visibilité d'une catégorie
  const handleToggleVisibility = async (category: Category) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    try {
      await categoryService.updateCategory(category.id, {
        isActive: !category.isActive
      });
      await loadCategories();
    } catch (err) {
      console.error('Erreur lors du changement de visibilité:', err);
      setError('Erreur lors du changement de visibilité. Veuillez réessayer.');
    }
  };

  // Déplacer une catégorie vers le haut
  const handleMoveUp = async (index: number) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    if (index <= 0) return; // Ne rien faire si déjà en haut
    
    try {
      setSavingOrder(true);
      const newCategories = [...categories];
      
      // Échanger avec l'élément précédent
      const current = newCategories[index];
      const previous = newCategories[index - 1];
      
      // Mettre à jour l'ordre
      const updates = [
        { id: current.id, order: previous.order ?? index - 1 },
        { id: previous.id, order: current.order ?? index }
      ];
      
      // Appliquer localement
      [newCategories[index - 1], newCategories[index]] = [newCategories[index], newCategories[index - 1]];
      setCategories(newCategories);
      
      // Enregistrer dans la base de données
      await categoryService.reorderCategories(updates);
    } catch (err) {
      console.error('Erreur lors du déplacement de la catégorie:', err);
      setError('Erreur lors du déplacement. Veuillez réessayer.');
      await loadCategories(); // Recharger pour revenir à l'état précédent
    } finally {
      setSavingOrder(false);
    }
  };

  // Déplacer une catégorie vers le bas
  const handleMoveDown = async (index: number) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    if (index >= categories.length - 1) return; // Ne rien faire si déjà en bas
    
    try {
      setSavingOrder(true);
      const newCategories = [...categories];
      
      // Échanger avec l'élément suivant
      const current = newCategories[index];
      const next = newCategories[index + 1];
      
      // Mettre à jour l'ordre
      const updates = [
        { id: current.id, order: next.order ?? index + 1 },
        { id: next.id, order: current.order ?? index }
      ];
      
      // Appliquer localement
      [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
      setCategories(newCategories);
      
      // Enregistrer dans la base de données
      await categoryService.reorderCategories(updates);
    } catch (err) {
      console.error('Erreur lors du déplacement de la catégorie:', err);
      setError('Erreur lors du déplacement. Veuillez réessayer.');
      await loadCategories(); // Recharger pour revenir à l'état précédent
    } finally {
      setSavingOrder(false);
    }
  };

  // Configuration des capteurs pour DnD
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Gestionnaire de fin de glisser-déposer
  const handleDragEnd = async (event: DragEndEvent) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    const { active, over } = event;
    
    if (!over) return;
    if (active.id === over.id) return;
    
    try {
      setSavingOrder(true);
      
      // Trouver les positions
      const oldIndex = categories.findIndex(cat => cat.id === active.id);
      const newIndex = categories.findIndex(cat => cat.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return;
      
      // Réorganiser le tableau localement
      const newCategories = arrayMove(categories, oldIndex, newIndex);
      setCategories(newCategories);
      
      // Préparer les mises à jour d'ordre pour la base de données
      const updates = newCategories.map((cat, index) => ({
        id: cat.id,
        order: index
      }));
      
      // Enregistrer dans la base de données
      await categoryService.reorderCategories(updates);
    } catch (err) {
      console.error('Erreur lors de la réorganisation:', err);
      setError('Erreur lors de la réorganisation. Veuillez réessayer.');
      await loadCategories(); // Recharger pour revenir à l'état précédent
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <Layout title="Gestion des Catégories">
      {activeScopeType !== 'store' || !selectedStoreId ? (
        <ScopeRequirementAlert
          requiredScope="store"
          title="Contexte magasin requis"
          description="Cette page gère le catalogue d'un magasin précis. Sélectionne un scope Magasin depuis la barre haute."
        />
      ) : (
        <>
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">
            Catégories
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddCategory}
            disabled={isCatalogReadOnly}
          >
            Ajouter une catégorie
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Gérez les catégories de produits de votre menu.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isCatalogReadOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {MENU_CATALOG_POSCAISSE_MESSAGE}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : savingOrder ? (
        <Box display="flex" justifyContent="center" my={4} flexDirection="column" alignItems="center">
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography>Enregistrement de l'ordre...</Typography>
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" width="48px"></TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Aucune catégorie trouvée. Commencez par en ajouter une !
                    </TableCell>
                  </TableRow>
                ) : (
                  <SortableContext 
                    items={paginatedCategories.map(cat => cat.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {paginatedCategories.map((category, index) => (
                      <SortableRow
                        key={category.id}
                        id={category.id}
                        category={category}
                        onToggleVisibility={handleToggleVisibility}
                        onEdit={handleEditCategory}
                        onDelete={handleDeleteCategory}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        index={index}
                        isFirst={index === 0}
                        isLast={index === categories.length - 1}
                        readOnly={isCatalogReadOnly}
                      />
                    ))}
                  </SortableContext>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Composant de pagination */}
          <TablePagination
            component="div"
            count={totalCategories}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Éléments par page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
          />
        </DndContext>
      )}

      {/* Dialogue pour ajouter/modifier une catégorie */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? `Modifier la catégorie: ${editingCategory.name}` : 'Ajouter une catégorie'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="name"
              label="Nom de la catégorie"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              name="order"
              label="Position d'affichage"
              type="number"
              value={formData.order || ''}
              onChange={handleInputChange}
              fullWidth
              InputProps={{ inputProps: { min: 0 } }}
              helperText="Position dans l'ordre d'affichage (0 = premier)"
            />
            <FormControlLabel
              control={
                <Switch
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  color="primary"
                />
              }
              label="Visible sur le menu"
            />
            <FormControlLabel
              control={
                <Switch
                  name="isAddonCategory"
                  checked={formData.isAddonCategory || false}
                  onChange={handleInputChange}
                  color="primary"
                />
              }
              label="Catégorie Add-on"
            />

            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Choisir une image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {formData.imagePreview && (
                <Box
                  component="img"
                  src={formData.imagePreview}
                  alt="Preview"
                  sx={{
                    display: 'block',
                    maxHeight: 200,
                    maxWidth: '100%',
                    my: 2
                  }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={savingCategory}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.name || isCatalogReadOnly || savingCategory}
            startIcon={savingCategory ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {savingCategory ? 'Enregistrement…' : editingCategory ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Layout>
  );
};

export default Categories;
